import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();

    // Scroll to section if hash is provided
    useEffect(() => {
        if (location.hash) {
            const element = document.getElementById(location.hash.substring(1));
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [location.hash]);

    return (
        <div className="privacy-policy-container">
            <div className="privacy-policy-content">
                <header className="privacy-header">
                    <h1>{t('privacy.title')}</h1>
                    <p className="last-updated">{t('privacy.lastUpdated')}: {new Date().toLocaleDateString()}</p>
                </header>

                <nav className="privacy-nav">
                    <h3>{t('privacy.tableOfContents')}</h3>
                    <ul>
                        <li><a href="#overview">{t('privacy.overview')}</a></li>
                        <li><a href="#information-collected">{t('privacy.informationCollected')}</a></li>
                        <li><a href="#how-we-use">{t('privacy.howWeUse')}</a></li>
                        <li><a href="#cookies">{t('privacy.cookies')}</a></li>
                        <li><a href="#third-party">{t('privacy.thirdParty')}</a></li>
                        <li><a href="#data-retention">{t('privacy.dataRetention')}</a></li>
                        <li><a href="#your-rights">{t('privacy.yourRights')}</a></li>
                        <li><a href="#security">{t('privacy.security')}</a></li>
                        <li><a href="#changes">{t('privacy.changes')}</a></li>
                        <li><a href="#contact">{t('privacy.contact')}</a></li>
                    </ul>
                </nav>

                <main className="privacy-main">
                    <section id="overview" className="privacy-section">
                        <h2>{t('privacy.overview')}</h2>
                        <p>{t('privacy.overviewText')}</p>
                        <p>{t('privacy.complianceText')}</p>
                    </section>

                    <section id="information-collected" className="privacy-section">
                        <h2>{t('privacy.informationCollected')}</h2>
                        
                        <h3>{t('privacy.personalInfo')}</h3>
                        <ul>
                            <li><strong>{t('privacy.accountInfo')}</strong>: {t('privacy.accountInfoDesc')}</li>
                            <li><strong>{t('privacy.profileInfo')}</strong>: {t('privacy.profileInfoDesc')}</li>
                            <li><strong>{t('privacy.communicationInfo')}</strong>: {t('privacy.communicationInfoDesc')}</li>
                        </ul>

                        <h3>{t('privacy.automaticInfo')}</h3>
                        <ul>
                            <li><strong>{t('privacy.deviceInfo')}</strong>: {t('privacy.deviceInfoDesc')}</li>
                            <li><strong>{t('privacy.usageInfo')}</strong>: {t('privacy.usageInfoDesc')}</li>
                            <li><strong>{t('privacy.locationInfo')}</strong>: {t('privacy.locationInfoDesc')}</li>
                            <li><strong>{t('privacy.performanceInfo')}</strong>: {t('privacy.performanceInfoDesc')}</li>
                        </ul>

                        <h3>{t('privacy.trackingDetails')}</h3>
                        <p>{t('privacy.trackingDetailsText')}</p>
                        <ul>
                            <li>{t('privacy.pageViews')}</li>
                            <li>{t('privacy.clickTracking')}</li>
                            <li>{t('privacy.searchQueries')}</li>
                            <li>{t('privacy.sessionDuration')}</li>
                            <li>{t('privacy.errorLogs')}</li>
                            <li>{t('privacy.apiCalls')}</li>
                        </ul>
                    </section>

                    <section id="how-we-use" className="privacy-section">
                        <h2>{t('privacy.howWeUse')}</h2>
                        <p>{t('privacy.howWeUseIntro')}</p>
                        
                        <h3>{t('privacy.serviceProvision')}</h3>
                        <ul>
                            <li>{t('privacy.serviceProvisionDesc1')}</li>
                            <li>{t('privacy.serviceProvisionDesc2')}</li>
                            <li>{t('privacy.serviceProvisionDesc3')}</li>
                        </ul>

                        <h3>{t('privacy.serviceImprovement')}</h3>
                        <ul>
                            <li>{t('privacy.serviceImprovementDesc1')}</li>
                            <li>{t('privacy.serviceImprovementDesc2')}</li>
                            <li>{t('privacy.serviceImprovementDesc3')}</li>
                        </ul>

                        <h3>{t('privacy.communication')}</h3>
                        <ul>
                            <li>{t('privacy.communicationDesc1')}</li>
                            <li>{t('privacy.communicationDesc2')}</li>
                            <li>{t('privacy.communicationDesc3')}</li>
                        </ul>

                        <h3>{t('privacy.legal')}</h3>
                        <ul>
                            <li>{t('privacy.legalDesc1')}</li>
                            <li>{t('privacy.legalDesc2')}</li>
                            <li>{t('privacy.legalDesc3')}</li>
                        </ul>
                    </section>

                    <section id="cookies" className="privacy-section">
                        <h2>{t('privacy.cookies')}</h2>
                        <p>{t('privacy.cookiesIntro')}</p>

                        <h3>{t('privacy.cookieTypes')}</h3>
                        
                        <div className="cookie-category">
                            <h4>{t('cookies.necessary')}</h4>
                            <p><strong>{t('privacy.purpose')}</strong>: {t('privacy.necessaryCookiesDesc')}</p>
                            <p><strong>{t('privacy.consent')}</strong>: {t('privacy.necessaryConsentDesc')}</p>
                            <p><strong>{t('privacy.examples')}</strong>:</p>
                            <ul>
                                <li>authentication_token - {t('privacy.authTokenDesc')}</li>
                                <li>session_id - {t('privacy.sessionIdDesc')}</li>
                                <li>theme_preference - {t('privacy.themeDesc')}</li>
                                <li>language_preference - {t('privacy.languageDesc')}</li>
                            </ul>
                        </div>

                        <div className="cookie-category">
                            <h4>{t('cookies.analytics')}</h4>
                            <p><strong>{t('privacy.purpose')}</strong>: {t('privacy.analyticsCookiesDesc')}</p>
                            <p><strong>{t('privacy.consent')}</strong>: {t('privacy.analyticsConsentDesc')}</p>
                            <p><strong>{t('privacy.examples')}</strong>:</p>
                            <ul>
                                <li>user_activity_tracking - {t('privacy.activityTrackingDesc')}</li>
                                <li>performance_monitoring - {t('privacy.performanceTrackingDesc')}</li>
                                <li>error_tracking - {t('privacy.errorTrackingDesc')}</li>
                                <li>sentry_session - {t('privacy.sentrySessionDesc')}</li>
                            </ul>
                        </div>

                        <div className="cookie-category">
                            <h4>{t('cookies.marketing')}</h4>
                            <p><strong>{t('privacy.purpose')}</strong>: {t('privacy.marketingCookiesDesc')}</p>
                            <p><strong>{t('privacy.consent')}</strong>: {t('privacy.marketingConsentDesc')}</p>
                            <p><strong>{t('privacy.retention')}</strong>: {t('privacy.marketingRetentionDesc')}</p>
                        </div>

                        <div className="cookie-category">
                            <h4>{t('cookies.preferences')}</h4>
                            <p><strong>{t('privacy.purpose')}</strong>: {t('privacy.preferencesCookiesDesc')}</p>
                            <p><strong>{t('privacy.consent')}</strong>: {t('privacy.preferencesConsentDesc')}</p>
                            <p><strong>{t('privacy.examples')}</strong>:</p>
                            <ul>
                                <li>display_preferences - {t('privacy.displayPrefsDesc')}</li>
                                <li>search_history - {t('privacy.searchHistoryDesc')}</li>
                                <li>feature_preferences - {t('privacy.featurePrefsDesc')}</li>
                            </ul>
                        </div>

                        <h3>{t('privacy.cookieManagement')}</h3>
                        <p>{t('privacy.cookieManagementDesc')}</p>
                        <p>{t('privacy.cookieControlsDesc')}</p>
                    </section>

                    <section id="third-party" className="privacy-section">
                        <h2>{t('privacy.thirdParty')}</h2>
                        <p>{t('privacy.thirdPartyIntro')}</p>

                        <div className="third-party-service">
                            <h3>Sentry</h3>
                            <p><strong>{t('privacy.purpose')}</strong>: {t('privacy.sentryPurpose')}</p>
                            <p><strong>{t('privacy.dataShared')}</strong>: {t('privacy.sentryDataShared')}</p>
                            <p><strong>{t('privacy.privacyPolicy')}</strong>: <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer">https://sentry.io/privacy/</a></p>
                            <p><strong>{t('privacy.optOut')}</strong>: {t('privacy.sentryOptOut')}</p>
                        </div>

                        <div className="third-party-service">
                            <h3>ELK Stack (Elasticsearch)</h3>
                            <p><strong>{t('privacy.purpose')}</strong>: {t('privacy.elkPurpose')}</p>
                            <p><strong>{t('privacy.dataShared')}</strong>: {t('privacy.elkDataShared')}</p>
                            <p><strong>{t('privacy.retention')}</strong>: {t('privacy.elkRetention')}</p>
                        </div>

                        <div className="third-party-service">
                            <h3>MongoDB Atlas</h3>
                            <p><strong>{t('privacy.purpose')}</strong>: {t('privacy.mongoPurpose')}</p>
                            <p><strong>{t('privacy.dataShared')}</strong>: {t('privacy.mongoDataShared')}</p>
                            <p><strong>{t('privacy.privacyPolicy')}</strong>: <a href="https://www.mongodb.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">https://www.mongodb.com/legal/privacy-policy</a></p>
                        </div>

                        <div className="third-party-service">
                            <h3>Hetzner Cloud</h3>
                            <p><strong>{t('privacy.purpose')}</strong>: {t('privacy.hetznerPurpose')}</p>
                            <p><strong>{t('privacy.location')}</strong>: {t('privacy.hetznerLocation')}</p>
                            <p><strong>{t('privacy.privacyPolicy')}</strong>: <a href="https://www.hetzner.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">https://www.hetzner.com/legal/privacy-policy</a></p>
                        </div>
                    </section>

                    <section id="data-retention" className="privacy-section">
                        <h2>{t('privacy.dataRetention')}</h2>
                        <p>{t('privacy.dataRetentionIntro')}</p>

                        <table className="retention-table">
                            <thead>
                                <tr>
                                    <th>{t('privacy.dataType')}</th>
                                    <th>{t('privacy.retentionPeriod')}</th>
                                    <th>{t('privacy.reason')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{t('privacy.accountData')}</td>
                                    <td>{t('privacy.accountDataRetention')}</td>
                                    <td>{t('privacy.accountDataReason')}</td>
                                </tr>
                                <tr>
                                    <td>{t('privacy.activityLogs')}</td>
                                    <td>{t('privacy.activityLogsRetention')}</td>
                                    <td>{t('privacy.activityLogsReason')}</td>
                                </tr>
                                <tr>
                                    <td>{t('privacy.errorLogs')}</td>
                                    <td>{t('privacy.errorLogsRetention')}</td>
                                    <td>{t('privacy.errorLogsReason')}</td>
                                </tr>
                                <tr>
                                    <td>{t('privacy.sessionData')}</td>
                                    <td>{t('privacy.sessionDataRetention')}</td>
                                    <td>{t('privacy.sessionDataReason')}</td>
                                </tr>
                                <tr>
                                    <td>{t('privacy.marketingData')}</td>
                                    <td>{t('privacy.marketingDataRetention')}</td>
                                    <td>{t('privacy.marketingDataReason')}</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>

                    <section id="your-rights" className="privacy-section">
                        <h2>{t('privacy.yourRights')}</h2>
                        <p>{t('privacy.yourRightsIntro')}</p>

                        <div className="rights-grid">
                            <div className="right-item">
                                <h3>{t('privacy.rightAccess')}</h3>
                                <p>{t('privacy.rightAccessDesc')}</p>
                                <Link to="/account/data-export" className="right-action">{t('privacy.requestData')}</Link>
                            </div>

                            <div className="right-item">
                                <h3>{t('privacy.rightRectification')}</h3>
                                <p>{t('privacy.rightRectificationDesc')}</p>
                                <Link to="/account/profile" className="right-action">{t('privacy.updateProfile')}</Link>
                            </div>

                            <div className="right-item">
                                <h3>{t('privacy.rightErasure')}</h3>
                                <p>{t('privacy.rightErasureDesc')}</p>
                                <Link to="/account/delete" className="right-action">{t('privacy.deleteAccount')}</Link>
                            </div>

                            <div className="right-item">
                                <h3>{t('privacy.rightPortability')}</h3>
                                <p>{t('privacy.rightPortabilityDesc')}</p>
                                <Link to="/account/data-export" className="right-action">{t('privacy.exportData')}</Link>
                            </div>

                            <div className="right-item">
                                <h3>{t('privacy.rightObject')}</h3>
                                <p>{t('privacy.rightObjectDesc')}</p>
                                <Link to="/account/privacy-settings" className="right-action">{t('privacy.privacySettings')}</Link>
                            </div>

                            <div className="right-item">
                                <h3>{t('privacy.rightRestrict')}</h3>
                                <p>{t('privacy.rightRestrictDesc')}</p>
                                <a href="mailto:privacy@lularge.com" className="right-action">{t('privacy.contactUs')}</a>
                            </div>
                        </div>

                        <div className="consent-management">
                            <h3>{t('privacy.consentManagement')}</h3>
                            <p>{t('privacy.consentManagementDesc')}</p>
                            <button 
                                className="consent-button"
                                onClick={() => {
                                    // Trigger cookie banner to reappear
                                    localStorage.removeItem('cookieConsent');
                                    window.location.reload();
                                }}
                            >
                                {t('privacy.manageCookies')}
                            </button>
                        </div>
                    </section>

                    <section id="security" className="privacy-section">
                        <h2>{t('privacy.security')}</h2>
                        <p>{t('privacy.securityIntro')}</p>

                        <h3>{t('privacy.securityMeasures')}</h3>
                        <ul>
                            <li><strong>{t('privacy.encryption')}</strong>: {t('privacy.encryptionDesc')}</li>
                            <li><strong>{t('privacy.accessControls')}</strong>: {t('privacy.accessControlsDesc')}</li>
                            <li><strong>{t('privacy.monitoring')}</strong>: {t('privacy.monitoringDesc')}</li>
                            <li><strong>{t('privacy.backups')}</strong>: {t('privacy.backupsDesc')}</li>
                            <li><strong>{t('privacy.incidentResponse')}</strong>: {t('privacy.incidentResponseDesc')}</li>
                        </ul>

                        <h3>{t('privacy.dataTransfer')}</h3>
                        <p>{t('privacy.dataTransferDesc')}</p>

                        <h3>{t('privacy.breachNotification')}</h3>
                        <p>{t('privacy.breachNotificationDesc')}</p>
                    </section>

                    <section id="changes" className="privacy-section">
                        <h2>{t('privacy.changes')}</h2>
                        <p>{t('privacy.changesDesc')}</p>
                        <p>{t('privacy.notificationDesc')}</p>
                        
                        <h3>{t('privacy.versionHistory')}</h3>
                        <ul>
                            <li><strong>v1.0</strong> ({new Date().toLocaleDateString()}) - {t('privacy.initialVersion')}</li>
                        </ul>
                    </section>

                    <section id="contact" className="privacy-section">
                        <h2>{t('privacy.contact')}</h2>
                        <p>{t('privacy.contactIntro')}</p>

                        <div className="contact-info">
                            <div className="contact-method">
                                <h3>{t('privacy.email')}</h3>
                                <p><a href="mailto:privacy@lularge.com">privacy@lularge.com</a></p>
                                <p><a href="mailto:support@lularge.com">support@lularge.com</a></p>
                            </div>

                            <div className="contact-method">
                                <h3>{t('privacy.dataProtectionOfficer')}</h3>
                                <p><a href="mailto:dpo@lularge.com">dpo@lularge.com</a></p>
                            </div>

                            <div className="contact-method">
                                <h3>{t('privacy.responseTime')}</h3>
                                <p>{t('privacy.responseTimeDesc')}</p>
                            </div>
                        </div>

                        <h3>{t('privacy.supervisoryAuthority')}</h3>
                        <p>{t('privacy.supervisoryAuthorityDesc')}</p>
                    </section>
                </main>

                <footer className="privacy-footer">
                    <p>{t('privacy.footerText')}</p>
                    <div className="footer-links">
                        <Link to="/terms">{t('privacy.termsOfService')}</Link>
                        <Link to="/cookies">{t('privacy.cookiePolicy')}</Link>
                        <Link to="/contact">{t('privacy.contactPage')}</Link>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default PrivacyPolicy; 